/**
 * NIL Go: which college deals must be reported, by when, and what is held until
 * they clear. An eligibility rule, so every line here says what it rests on.
 *
 * FROM THE COLLEGE SPORTS COMMISSION (rules summary Oct 30 2025; NCAA Bylaw
 * 22.2.2; FAQ):
 *   - Division I athletes report third-party NIL deals with "a total value of
 *     $600 in the aggregate or more";
 *   - written documentation goes to NIL Go "within five business days of
 *     execution or agreement to payment terms";
 *   - the penalty for not reporting is ineligibility;
 *   - a deal comes back cleared, not cleared, or flagged for more review, and
 *     one not cleared can be revised and resubmitted, cancelled with any money
 *     refunded, or taken to neutral arbitration.
 *
 * REPPR'S OWN JUDGEMENT, and not the Commission's:
 *   - THE HOLD. The rules found do not say payment and posting must wait for
 *     clearance; the stated risk is going ahead with a deal not cleared. Agencies
 *     asked for the cautious default: recording a payment or a posted deliverable
 *     is blocked until the deal clears, and an admin may override with a reason,
 *     which is kept. Jack Tabb chose this on Nov 4 2026.
 *   - THE $600. The FAQ does not say how the aggregate is counted, so deals with
 *     the same payer are added together, and a deal with no value on file counts
 *     as over: an unknown must not read as exempt.
 *   - THE DEADLINE. Weekends are skipped and holidays are not, so it can only come
 *     early. The clock runs from the day recorded, or else the day the deal
 *     reached Agreed, in US Eastern, and says it needs confirming.
 *
 * NOT COVERED: incoming high school, junior college and transfer athletes' earlier
 * deals, which have their own 14-day rule, and school revenue-share agreements,
 * which go through CAPS (an admin marks those "not required", with the reason).
 *
 * Reppr does not file anything: no public way to submit was found, and the athlete
 * is the one required to report. It finds the deal, runs the clock and keeps the
 * status. Pure apart from assertNotHeld, which takes whichever client is asking.
 */

export const NIL_GO_THRESHOLD = 600
export const REPORT_BUSINESS_DAYS = 5
export const NIL_GO_INFO_URL = 'https://www.collegesportscommission.org/focus-areas/nil/'

export const NIL_GO_STATUSES = ['not_reported', 'reported', 'cleared', 'flagged', 'not_cleared', 'not_required'] as const
export type NilGoStatus = typeof NIL_GO_STATUSES[number]

export const NIL_GO_STATUS_LABEL: Record<NilGoStatus, string> = {
  not_reported: 'Not reported yet',
  reported: 'Reported, waiting on NIL Go',
  cleared: 'Cleared',
  flagged: 'Flagged for more review',
  not_cleared: 'Not cleared',
  not_required: 'Marked not required',
}

/** The stages at which payment terms have been agreed, so the clock is running. */
export const CLOCK_STAGES = ['Agreed', 'Contract Sent', 'Executed'] as const

export interface NilGoDeal {
  id: string
  title?: string | null
  client_id: string
  organization_id?: string | null
  value?: number | null
  stage: string
  deal_type?: string | null
  stage_changed_at?: string | null
}

export interface NilGoRecord {
  id?: string
  deal_id: string
  status: NilGoStatus
  agreed_on?: string | null
  agreed_on_confirmed?: boolean
  reported_on?: string | null
  decided_on?: string | null
  reference?: string | null
  note?: string | null
  override_by?: string | null
  override_at?: string | null
  override_reason?: string | null
}

export interface NilGoState {
  deal_id: string
  client_id: string
  deal_title: string | null
  status: NilGoStatus
  /** Payment terms are agreed, so the five days are running. */
  clock_running: boolean
  /** YYYY-MM-DD, or null before the clock runs. */
  agreed_on: string | null
  /** A person, not a stage change, gave that date. */
  agreed_on_confirmed: boolean
  deadline: string | null
  /** Business days left; negative once overdue. Null when reporting is not outstanding. */
  business_days_left: number | null
  overdue: boolean
  /** Recording a payment or a posted deliverable is blocked. */
  hold: boolean
  override: { by: string | null; at: string | null; reason: string } | null
  /** No value on file, so it is treated as over the threshold. */
  value_unknown: boolean
  reported_on: string | null
  decided_on: string | null
  reference: string | null
  note: string | null
}

/**
 * NCAA Division I, by the league on the record. An old "College" or "NCAA" row
 * names no division and counts as D1, since missing one risks eligibility and a
 * false alarm costs a look. D2, D3, NAIA, JUCO and high school do not count.
 */
export function isDivisionOne(league: string | null | undefined): boolean {
  const l = String(league ?? '').toLowerCase()
  if (/\bd2\b|\bd3\b|naia|juco|njcaa|high school/.test(l)) return false
  return /college|ncaa|\bd1\b|fbs|fcs/.test(l)
}

const isWeekend = (d: Date) => d.getUTCDay() === 0 || d.getUTCDay() === 6
const at = (day: string) => new Date(`${day}T12:00:00Z`)
const iso = (d: Date) => d.toISOString().slice(0, 10)

/** The day the clock's Nth business day falls on. The day of agreement is not counted. */
export function addBusinessDays(day: string, n: number): string {
  const d = at(day)
  for (let left = n; left > 0;) {
    d.setUTCDate(d.getUTCDate() + 1)
    if (!isWeekend(d)) left--
  }
  return iso(d)
}

/** Business days from today to the deadline; negative once it has passed. */
export function businessDaysUntil(today: string, deadline: string): number {
  if (today === deadline) return 0
  const forward = today < deadline
  const d = at(today)
  const end = at(deadline)
  let count = 0
  while (iso(d) !== iso(end)) {
    d.setUTCDate(d.getUTCDate() + (forward ? 1 : -1))
    // Counting back, the day being left is the one that counts as overdue.
    if (!isWeekend(d)) count++
  }
  return forward ? count : -count
}

/** A stored instant as the calendar day in US Eastern, where the deadline will be read. */
export function easternDay(instant: string | Date): string | null {
  const d = instant instanceof Date ? instant : new Date(String(instant).replace(' ', 'T'))
  if (Number.isNaN(d.getTime())) return null
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
}

/** Whether this deal must be reported at all: the same payer's deals added together. */
export function isReportable(deal: NilGoDeal, siblings: NilGoDeal[]): { reportable: boolean; value_unknown: boolean } {
  if (deal.stage === 'Lost' || deal.deal_type === 'playing_contract') return { reportable: false, value_unknown: false }
  const samePayer = deal.organization_id
    ? siblings.filter(d => d.client_id === deal.client_id && d.organization_id === deal.organization_id && d.stage !== 'Lost' && d.deal_type !== 'playing_contract')
    : [deal]
  const considered = samePayer.some(d => d.id === deal.id) ? samePayer : [...samePayer, deal]
  if (considered.some(d => d.value === null || d.value === undefined)) return { reportable: true, value_unknown: true }
  return { reportable: considered.reduce((sum, d) => sum + Number(d.value), 0) >= NIL_GO_THRESHOLD, value_unknown: false }
}

/** The state of one deal, or null when NIL Go has nothing to say about it. */
export function nilGoState(deal: NilGoDeal, league: string | null | undefined, siblings: NilGoDeal[], record: NilGoRecord | null | undefined, today: string): NilGoState | null {
  if (!isDivisionOne(league)) return null
  const { reportable, value_unknown } = isReportable(deal, siblings)
  if (!reportable) return null

  const clock_running = (CLOCK_STAGES as readonly string[]).includes(deal.stage)
  const recorded = record?.agreed_on ? String(record.agreed_on).slice(0, 10) : null
  const agreed_on = recorded ?? (clock_running && deal.stage_changed_at ? easternDay(deal.stage_changed_at) : null)
  const status: NilGoStatus = record?.status ?? 'not_reported'
  const deadline = clock_running && agreed_on ? addBusinessDays(agreed_on, REPORT_BUSINESS_DAYS) : null
  const outstanding = status === 'not_reported' && deadline !== null
  const business_days_left = outstanding ? businessDaysUntil(today, deadline!) : null
  const override = record?.override_at && record.override_reason ? { by: record.override_by ?? null, at: record.override_at, reason: record.override_reason } : null

  return {
    deal_id: deal.id,
    client_id: deal.client_id,
    deal_title: deal.title ?? null,
    status,
    clock_running,
    agreed_on,
    agreed_on_confirmed: record?.agreed_on_confirmed === true,
    deadline,
    business_days_left,
    overdue: business_days_left !== null && business_days_left < 0,
    hold: clock_running && status !== 'cleared' && status !== 'not_required' && !override,
    override,
    value_unknown,
    reported_on: record?.reported_on ? String(record.reported_on).slice(0, 10) : null,
    decided_on: record?.decided_on ? String(record.decided_on).slice(0, 10) : null,
    reference: record?.reference || null,
    note: record?.note || null,
  }
}

/** Every deal NIL Go has something to say about, for a whole agency's records. */
export function nilGoStates(clients: { id: string; league?: string | null }[], deals: NilGoDeal[], records: NilGoRecord[], today: string): NilGoState[] {
  const leagueOf = new Map(clients.map(c => [c.id, c.league]))
  const recordOf = new Map(records.map(r => [r.deal_id, r]))
  const out: NilGoState[] = []
  for (const deal of deals) {
    const state = nilGoState(deal, leagueOf.get(deal.client_id), deals, recordOf.get(deal.id), today)
    if (state) out.push(state)
  }
  return out
}

const longDay = (day: string) => at(day).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })

/** The deadline in words. */
export function deadlineWords(s: NilGoState): string {
  if (!s.deadline) return 'The clock starts when payment terms are agreed.'
  if (s.business_days_left === null) return `Was due ${longDay(s.deadline)}.`
  if (s.overdue) return `Overdue: it was due ${longDay(s.deadline)}. Report it now; the penalty for not reporting is ineligibility.`
  if (s.business_days_left === 0) return `Due today, ${longDay(s.deadline)}.`
  return `Due ${longDay(s.deadline)}, ${s.business_days_left} business day${s.business_days_left === 1 ? '' : 's'} from now.`
}

export class NilGoHoldError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'NilGoHoldError'
  }
}

export function holdMessage(s: NilGoState, what: string): string {
  return `${what} is on hold: this deal has not cleared NIL Go (${NIL_GO_STATUS_LABEL[s.status].toLowerCase()}). Update its status on the deal, or an admin can override the hold there with a reason.`
}

type Pb = {
  collection: (name: string) => {
    getOne: (id: string, opts?: any) => Promise<any>
    getFullList: (opts?: any) => Promise<any[]>
  }
}
const DEAL_FIELDS = 'id,title,client_id,organization_id,value,stage,deal_type,stage_changed_at'

/** The state of one deal, read now. */
export async function readNilGoState(pb: Pb, dealId: string, now = new Date()): Promise<NilGoState | null> {
  const deal = await pb.collection('deals').getOne(dealId, { fields: DEAL_FIELDS })
  const client = await pb.collection('clients').getOne(deal.client_id, { fields: 'id,league' })
  if (!isDivisionOne(client.league)) return null
  const id = (v: string) => String(v).replace(/["'\\]/g, '')
  const siblings = deal.organization_id
    ? await pb.collection('deals').getFullList({ filter: `client_id = "${id(deal.client_id)}" && organization_id = "${id(deal.organization_id)}"`, fields: DEAL_FIELDS })
    : [deal]
  const [record] = await pb.collection('nil_go_records').getFullList({ filter: `deal_id = "${id(dealId)}"` })
  return nilGoState(deal, client.league, siblings, record ?? null, easternDay(now) as string)
}

/** Stops a payment or a posted deliverable being recorded on a held deal. */
export async function assertNotHeld(pb: Pb, dealId: string, what: string): Promise<void> {
  const state = await readNilGoState(pb, dealId)
  if (state?.hold) throw new NilGoHoldError(holdMessage(state, what))
}

/** Which payments this new schedule marks paid that the old one did not. */
export function newlyPaid(before: any[] | null | undefined, after: any[] | null | undefined): boolean {
  const key = (p: any) => `${String(p?.date ?? '').slice(0, 10)}|${Number(p?.amount)}`
  const paid = new Set((before ?? []).filter(p => p?.status === 'paid').map(key))
  return (after ?? []).some(p => p?.status === 'paid' && !paid.has(key(p)))
}

/** Whether this new deliverables list moves anything forward to approved or posted. Taking one back is never held. */
export function newlyPosted(before: any[] | null | undefined, after: any[] | null | undefined): boolean {
  const rank = (status: unknown) => (status === 'done' ? 2 : status === 'approved' ? 1 : 0)
  const was = new Map((before ?? []).map(d => [d?.id, rank(d?.status)]))
  return (after ?? []).some(d => rank(d?.status) > 0 && rank(d?.status) > (was.get(d?.id) ?? 0))
}
